import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DEFAULT_DOD_CHECKS,
  type CodebaseDTO,
  type CodebaseDependencyDTO,
  type DodCheck,
  type ProjectDetailDTO,
} from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import {
  Btn,
  Chip,
  EmptyState,
  Field,
  Panel,
  SectionHeader,
  inputClass,
} from "@/design/primitives";
import {
  canManage,
  useApplications,
  useCodebases,
  useEnvironments,
  useProjectRole,
} from "../project-data";

/** DoD editor: řádek = `key | popis | příkaz?` (spec §53). */
function dodToLines(checks: DodCheck[]): string {
  return checks
    .map((c) => [c.key, c.label, c.command].filter(Boolean).join(" | "))
    .join("\n");
}

function parseDodLines(text: string): DodCheck[] {
  const checks: DodCheck[] = [];
  for (const line of text.split("\n")) {
    const [rawKey, label, command] = line.split("|").map((s) => s.trim());
    if (!rawKey) continue;
    const key = rawKey
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "-")
      .replace(/^-+/, "")
      .slice(0, 40);
    if (!key) continue;
    checks.push({
      key,
      label: label || rawKey,
      ...(command ? { command } : {}),
    });
  }
  return checks;
}

/** CODEBASES dle /Design: karty repozitářů + Codebase Map se závislostmi. */
export function CodebasesTab({ project }: { project: ProjectDetailDTO }) {
  const codebases = useCodebases(project.id);
  const role = useProjectRole(project);
  const manager = canManage(role);
  const [creating, setCreating] = useState(false);
  const list = codebases.data?.codebases ?? [];
  const deps = codebases.data?.dependencies ?? [];

  return (
    <div className="mt-4 grid grid-cols-1 items-start gap-4 xl:grid-cols-[1fr_400px]">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="af-label-dim">
            CODEBASES // PROJEKT ≠ REPO — {list.length} repozitářů
          </div>
          {manager ? (
            <Btn onClick={() => setCreating(true)} data-testid="codebase-new">
              {cs.codebases.newCodebase}
            </Btn>
          ) : null}
        </div>

        {creating ? (
          <NewCodebaseForm
            project={project}
            onClose={() => setCreating(false)}
          />
        ) : null}

        {list.length === 0 && !creating ? (
          <Panel className="p-8">
            <EmptyState
              title={cs.codebases.empty}
              note={cs.codebases.emptyNote}
            />
          </Panel>
        ) : (
          list.map((c) => (
            <CodebaseCard
              key={c.id}
              codebase={c}
              manager={manager}
              projectId={project.id}
            />
          ))
        )}
      </div>

      <CodebaseMapPanel
        project={project}
        codebases={list}
        dependencies={deps}
        manager={manager}
      />
    </div>
  );
}

function CodebaseCard({
  codebase,
  manager,
  projectId,
}: {
  codebase: CodebaseDTO;
  manager: boolean;
  projectId: string;
}) {
  const queryClient = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: () => api.deleteCodebase(codebase.id),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["section", projectId, "codebases"],
      }),
  });

  return (
    <Panel className="p-4" data-testid="codebase-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span
          className="af-display min-w-0 break-words text-[13px] font-semibold"
          style={{ color: "var(--text-primary)" }}
        >
          ⌥ {codebase.name}
        </span>
        <span className="flex min-w-0 items-center gap-3">
          <span
            className="min-w-0 break-all text-[11px]"
            style={{ color: "var(--accent)" }}
          >
            {codebase.repoUrl ?? "repo nepřipojeno"}
          </span>
          {manager ? (
            <button
              type="button"
              className="cursor-pointer text-[11px]"
              style={{ color: "var(--critical)" }}
              title="Smazat codebase"
              onClick={() => {
                if (confirm(`Smazat codebase ${codebase.name}?`))
                  deleteMutation.mutate();
              }}
            >
              ✕
            </button>
          ) : null}
        </span>
      </div>
      <div
        className="mt-1.5 text-[12px]"
        style={{ color: "var(--text-secondary)" }}
      >
        {codebase.description || "—"}
      </div>
      <div className="mt-2.5 grid grid-cols-1 gap-3 text-[11px] sm:grid-cols-3">
        <div>
          <span style={{ color: "var(--text-tertiary)" }}>
            {cs.codebases.branches}
          </span>
          <br />
          <span style={{ color: "var(--text-primary)" }}>
            {codebase.defaultBranch}
            {codebase.devBranch ? ` · ${codebase.devBranch}` : ""}
          </span>
        </div>
        <div>
          <span style={{ color: "var(--text-tertiary)" }}>
            {cs.codebases.policy}
          </span>
          <br />
          <span style={{ color: "var(--text-primary)" }}>
            {[codebase.branchPolicy, codebase.prPolicy]
              .filter(Boolean)
              .join(" · ") || "—"}
          </span>
        </div>
        <div>
          <span style={{ color: "var(--text-tertiary)" }}>
            {cs.codebases.stack}
          </span>
          <br />
          <span style={{ color: "var(--text-primary)" }}>
            {codebase.techStack.join(" · ") || "—"}
          </span>
        </div>
      </div>
      <LinksSection codebase={codebase} manager={manager} projectId={projectId} />

      <DodSection codebase={codebase} manager={manager} projectId={projectId} />
    </Panel>
  );
}

/** Aplikace a environmenty codebase — čipy + výběr pro managery. */
function LinksSection({
  codebase,
  manager,
  projectId,
}: {
  codebase: CodebaseDTO;
  manager: boolean;
  projectId: string;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [appIds, setAppIds] = useState<string[]>([]);
  const [envIds, setEnvIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: () =>
      api.updateCodebase(codebase.id, {
        applicationIds: appIds,
        environmentIds: envIds,
      }),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      void queryClient.invalidateQueries({
        queryKey: ["section", projectId, "codebases"],
      });
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : cs.common.error),
  });

  const empty =
    codebase.applications.length === 0 && codebase.environments.length === 0;

  return (
    <div data-testid="codebase-links">
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {codebase.applications.map((a) => (
            <Chip key={a.id} tone="cyan">
              {a.slug}
            </Chip>
          ))}
          {codebase.environments.map((e) => (
            <Chip key={e.id} tone="dim">
              {e.key}
            </Chip>
          ))}
          {empty && manager ? (
            <span
              className="text-[11px]"
              style={{ color: "var(--text-tertiary)" }}
            >
              bez aplikací a environmentů
            </span>
          ) : null}
        </div>
        {manager && !editing ? (
          <button
            type="button"
            className="cursor-pointer text-[11px]"
            style={{ color: "var(--accent)" }}
            onClick={() => {
              setAppIds(codebase.applications.map((a) => a.id));
              setEnvIds(codebase.environments.map((e) => e.id));
              setEditing(true);
            }}
            data-testid="codebase-links-edit"
          >
            ✎ PŘIŘADIT
          </button>
        ) : null}
      </div>
      {editing ? (
        <div>
          <LinkPicker
            projectId={projectId}
            appIds={appIds}
            envIds={envIds}
            onAppIds={setAppIds}
            onEnvIds={setEnvIds}
          />
          {error ? (
            <div
              className="mt-1 text-[11px]"
              style={{ color: "var(--critical)" }}
            >
              ⚠ {error}
            </div>
          ) : null}
          <div className="mt-1.5 flex justify-end gap-2">
            <Btn onClick={() => setEditing(false)}>{cs.common.cancel}</Btn>
            <Btn
              variant="primary"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
              data-testid="codebase-links-save"
            >
              {cs.common.save}
            </Btn>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Definition of Done codebase (spec §53) — čtení + editor pro managery. */
function DodSection({
  codebase,
  manager,
  projectId,
}: {
  codebase: CodebaseDTO;
  manager: boolean;
  projectId: string;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: () =>
      api.updateCodebase(codebase.id, { dodChecks: parseDodLines(text) }),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      void queryClient.invalidateQueries({
        queryKey: ["section", projectId, "codebases"],
      });
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : cs.common.error),
  });

  return (
    <div
      className="mt-3 pt-2.5"
      style={{ borderTop: "1px solid var(--border-subtle)" }}
      data-testid="codebase-dod"
    >
      <div className="flex items-center justify-between">
        <span className="af-label-dim">{cs.codebases.dod}</span>
        {manager && !editing ? (
          <button
            type="button"
            className="cursor-pointer text-[11px]"
            style={{ color: "var(--accent)" }}
            onClick={() => {
              setText(
                dodToLines(
                  codebase.dodChecks.length
                    ? codebase.dodChecks
                    : DEFAULT_DOD_CHECKS,
                ),
              );
              setEditing(true);
            }}
            data-testid="codebase-dod-edit"
          >
            ✎ UPRAVIT
          </button>
        ) : null}
      </div>
      {editing ? (
        <div className="mt-1.5">
          <textarea
            className={inputClass}
            rows={5}
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="codebase-dod-input"
          />
          <div
            className="mt-1 text-[11px]"
            style={{ color: "var(--text-tertiary)" }}
          >
            {cs.codebases.dodHint}
          </div>
          {error ? (
            <div
              className="mt-1 text-[11px]"
              style={{ color: "var(--critical)" }}
            >
              ⚠ {error}
            </div>
          ) : null}
          <div className="mt-1.5 flex justify-end gap-2">
            <Btn onClick={() => setEditing(false)}>{cs.common.cancel}</Btn>
            <Btn
              variant="primary"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
              data-testid="codebase-dod-save"
            >
              {cs.common.save}
            </Btn>
          </div>
        </div>
      ) : codebase.dodChecks.length === 0 ? (
        <div
          className="mt-1 text-[11px]"
          style={{ color: "var(--text-tertiary)" }}
        >
          {cs.codebases.dodDefault}
        </div>
      ) : (
        <div className="mt-1.5 flex flex-col gap-1">
          {codebase.dodChecks.map((c) => (
            <div
              key={c.key}
              className="flex flex-wrap justify-between gap-x-2 text-[11px]"
            >
              <span
                className="min-w-0 break-words"
                style={{ color: "var(--text-primary)" }}
              >
                ▸ {c.label}
              </span>
              {c.command ? (
                <span
                  className="break-all font-mono text-[11px]"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  {c.command}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Vrstvený graf: uzly bez odchozích závislostí dole, závislé nad nimi.
 * Nejvíc "depended" uzel dostává glow (jádro systému) — dle /Design.
 */
function CodebaseMapPanel({
  project,
  codebases,
  dependencies,
  manager,
}: {
  project: ProjectDetailDTO;
  codebases: CodebaseDTO[];
  dependencies: CodebaseDependencyDTO[];
  manager: boolean;
}) {
  const queryClient = useQueryClient();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [label, setLabel] = useState("");

  const addDep = useMutation({
    mutationFn: () =>
      api.setCodebaseDependency(from, {
        toCodebaseId: to,
        label: label || null,
      }),
    onSuccess: () => {
      setFrom("");
      setTo("");
      setLabel("");
      void queryClient.invalidateQueries({
        queryKey: ["section", project.id, "codebases"],
      });
    },
  });

  const layers = useMemo(
    () => buildLayers(codebases, dependencies),
    [codebases, dependencies],
  );
  const inDegree = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of dependencies) m.set(d.toId, (m.get(d.toId) ?? 0) + 1);
    return m;
  }, [dependencies]);
  const coreId = useMemo(() => {
    let best: string | null = null;
    let bestCount = 0;
    for (const [id, count] of inDegree) {
      if (count > bestCount) {
        best = id;
        bestCount = count;
      }
    }
    return best;
  }, [inDegree]);
  const core = codebases.find((c) => c.id === coreId);

  /**
   * Hrany se počítají ze skutečných pozic krabiček, ne z mřížky: uzly se
   * zalamují podle šířky panelu, takže napevno nakreslená čára by minula.
   * Bez čar to nebyla mapa, jen sloupec krabiček (uživatelský report).
   */
  const wrapRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef(new Map<string, HTMLDivElement>());
  const [edges, setEdges] = useState<MapEdge[]>([]);

  useLayoutEffect(() => {
    const measure = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const box = wrap.getBoundingClientRect();
      const at = (id: string) => {
        const el = nodeRefs.current.get(id);
        if (!el) return null;
        const r = el.getBoundingClientRect();
        return {
          x: r.left - box.left,
          y: r.top - box.top,
          w: r.width,
          h: r.height,
        };
      };
      const next: MapEdge[] = [];
      for (const d of dependencies) {
        const a = at(d.fromId);
        const b = at(d.toId);
        if (!a || !b) continue;
        const ax = a.x + a.w / 2;
        const bx = b.x + b.w / 2;
        if (b.y > a.y + a.h - 1) {
          // závislost míří o patro níž: svislá křivka mezi hranami krabiček
          const ay = a.y + a.h;
          const by = b.y;
          const mid = (ay + by) / 2;
          next.push({
            id: d.id,
            d: `M ${ax} ${ay} C ${ax} ${mid}, ${bx} ${mid}, ${bx} ${by}`,
            labelX: (ax + bx) / 2,
            labelY: mid - 3,
            label: d.label,
          });
        } else {
          // stejné patro (nebo cyklus): oblouk stranou, ať čára nejde skrz uzly
          const ay = a.y + a.h / 2;
          const by = b.y + b.h / 2;
          const dir = bx >= ax ? 1 : -1;
          const from = ax + dir * (a.w / 2);
          const to = bx - dir * (b.w / 2);
          const bow = Math.max(18, Math.abs(to - from) / 3);
          next.push({
            id: d.id,
            d: `M ${from} ${ay} C ${from + dir * bow} ${ay - bow}, ${to - dir * bow} ${by - bow}, ${to} ${by}`,
            labelX: (from + to) / 2,
            labelY: Math.min(ay, by) - bow / 2,
            label: d.label,
          });
        }
      }
      setEdges(next);
    };

    measure();
    const wrap = wrapRef.current;
    const ro = new ResizeObserver(measure);
    if (wrap) ro.observe(wrap);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [dependencies, layers, core]);

  return (
    <Panel className="p-4" data-testid="codebase-map">
      <SectionHeader label={cs.codebases.map} right={cs.codebases.mapBy} />
      <div
        className="mt-1 text-[11px]"
        style={{ color: "var(--text-tertiary)" }}
      >
        {cs.codebases.mapNote}
      </div>

      {codebases.length < 2 ? (
        <div
          className="py-4 text-[11px]"
          style={{ color: "var(--text-secondary)" }}
        >
          {cs.codebases.mapEmpty}
        </div>
      ) : (
        <div ref={wrapRef} className="relative mt-3 flex flex-col items-center">
          {/* hrany se kreslí pod uzly — čára na uzel je mapa, sloupec krabiček ne */}
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full"
            aria-hidden
            data-testid="codebase-map-edges"
          >
            <defs>
              <marker
                id="cb-arrow"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path
                  d="M 0 0 L 10 5 L 0 10 z"
                  fill="color-mix(in srgb, var(--accent) 55%, transparent)"
                />
              </marker>
            </defs>
            {edges.map((e) => (
              <g key={e.id}>
                <path
                  d={e.d}
                  fill="none"
                  stroke="color-mix(in srgb, var(--accent) 45%, transparent)"
                  strokeWidth={1.2}
                  markerEnd="url(#cb-arrow)"
                />
                {e.label ? (
                  <text
                    x={e.labelX}
                    y={e.labelY}
                    textAnchor="middle"
                    fontSize={10}
                    fill="var(--text-tertiary)"
                  >
                    {e.label}
                  </text>
                ) : null}
              </g>
            ))}
          </svg>
          {layers.map((layer, i) => (
            <div key={i} className="flex flex-col items-center">
              {i > 0 ? <div className="h-9" /> : null}
              <div className="flex flex-wrap justify-center gap-3">
                {layer.map((c) => (
                  <div
                    key={c.id}
                    ref={(el) => {
                      if (el) nodeRefs.current.set(c.id, el);
                      else nodeRefs.current.delete(c.id);
                    }}
                    className="relative px-3.5 py-2 text-[11px]"
                    style={
                      c.id === coreId
                        ? {
                            borderRadius: "var(--radius-box)",
                            border: "1px solid var(--accent)",
                            background: "var(--surface)",
                            boxShadow:
                              "0 0 14px color-mix(in srgb, var(--accent) 25%, transparent)",
                            color: "var(--accent)",
                          }
                        : {
                            borderRadius: "var(--radius-box)",
                            border:
                              "1px solid color-mix(in srgb, var(--accent) 25%, transparent)",
                            background: "var(--surface)",
                            color: "var(--text-primary)",
                          }
                    }
                  >
                    {c.slug}
                  </div>
                ))}
              </div>
            </div>
          ))}
          {core && core.applications.length > 0 ? (
            <>
              <svg width="200" height="30" viewBox="0 0 200 30" aria-hidden>
                <line
                  x1="100"
                  y1="2"
                  x2="100"
                  y2="28"
                  stroke="color-mix(in srgb, var(--accent) 40%, transparent)"
                  strokeDasharray="3 3"
                />
              </svg>
              <div className="flex flex-wrap justify-center gap-3">
                {core.applications.map((a) => (
                  <div
                    key={a.id}
                    className="px-3.5 py-2 text-[11px]"
                    style={{
                      borderRadius: "var(--radius-box)",
                      border:
                        "1px dashed color-mix(in srgb, var(--accent) 25%, transparent)",
                      color: "var(--text-primary)",
                    }}
                  >
                    {a.slug}{" "}
                    <span style={{ color: "var(--text-tertiary)" }}>
                      {cs.codebases.inRepo(core.slug)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : null}
          {dependencies.length === 0 ? (
            <div
              className="mt-3 text-[11px]"
              style={{ color: "var(--text-tertiary)" }}
            >
              {cs.codebases.mapNoDeps}
            </div>
          ) : (
            <div
              className="mt-3 text-[11px] leading-relaxed"
              style={{ color: "var(--text-secondary)" }}
            >
              {dependencies.map((d) => (
                <div key={d.id}>
                  {d.fromSlug} → {d.toSlug}
                  {d.label ? ` · ${d.label}` : ""}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {manager && codebases.length >= 2 ? (
        <div
          className="mt-4 pt-3"
          style={{ borderTop: "1px solid var(--border-subtle)" }}
        >
          <div className="af-label-dim mb-2">{cs.codebases.addDep}</div>
          <div className="flex flex-col gap-2 md:flex-row md:items-end">
            <select
              className={inputClass}
              style={{ background: "var(--surface-inset)" }}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            >
              <option value="">z…</option>
              {codebases.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.slug}
                </option>
              ))}
            </select>
            <span
              className="self-center text-[12px] md:self-auto md:pb-2"
              style={{ color: "var(--accent)" }}
            >
              →
            </span>
            <select
              className={inputClass}
              style={{ background: "var(--surface-inset)" }}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            >
              <option value="">na…</option>
              {codebases
                .filter((c) => c.id !== from)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.slug}
                  </option>
                ))}
            </select>
            <input
              className={inputClass}
              placeholder="popisek"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <Btn
              disabled={!from || !to || addDep.isPending}
              onClick={() => addDep.mutate()}
            >
              +
            </Btn>
          </div>
        </div>
      ) : null}
    </Panel>
  );
}

/** Longest-path layering: sinky (nikam nevolají) dole, závislé vrstvy nad nimi. */
/** Jedna hrana mapy: cesta v souřadnicích obalu + volitelný popisek. */
interface MapEdge {
  id: string;
  d: string;
  labelX: number;
  labelY: number;
  label: string | null;
}

function buildLayers(
  codebases: CodebaseDTO[],
  deps: CodebaseDependencyDTO[],
): CodebaseDTO[][] {
  const depth = new Map<string, number>();
  const outgoing = new Map<string, string[]>();
  for (const d of deps) {
    outgoing.set(d.fromId, [...(outgoing.get(d.fromId) ?? []), d.toId]);
  }
  const visit = (id: string, seen: Set<string>): number => {
    if (depth.has(id)) return depth.get(id)!;
    if (seen.has(id)) return 0; // cyklus — nechceme nekonečno
    seen.add(id);
    const targets = outgoing.get(id) ?? [];
    const value =
      targets.length === 0
        ? 0
        : Math.max(...targets.map((t) => visit(t, seen))) + 1;
    depth.set(id, value);
    return value;
  };
  for (const c of codebases) visit(c.id, new Set());
  const maxDepth = Math.max(0, ...depth.values());
  const layers: CodebaseDTO[][] = [];
  for (let d = maxDepth; d >= 0; d--) {
    const layer = codebases.filter((c) => (depth.get(c.id) ?? 0) === d);
    if (layer.length) layers.push(layer);
  }
  return layers;
}

// ---------------------------------------------------------------------------

function NewCodebaseForm({
  project,
  onClose,
}: {
  project: ProjectDetailDTO;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [repoUrl, setRepoUrl] = useState("");
  const [description, setDescription] = useState("");
  const [defaultBranch, setDefaultBranch] = useState("main");
  const [devBranch, setDevBranch] = useState("");
  const [stack, setStack] = useState("");
  const [dodText, setDodText] = useState(dodToLines(DEFAULT_DOD_CHECKS));
  const [appIds, setAppIds] = useState<string[]>([]);
  const [envIds, setEnvIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.createCodebase(project.id, {
        name,
        description,
        repoUrl: repoUrl.trim() || null,
        defaultBranch: defaultBranch.trim() || "main",
        devBranch: devBranch.trim() || null,
        techStack: stack
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        dodChecks: parseDodLines(dodText),
        applicationIds: appIds,
        environmentIds: envIds,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["section", project.id, "codebases"],
      });
      onClose();
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : cs.common.error),
  });

  return (
    <Panel brackets className="p-4" data-testid="codebase-new-form">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={cs.fields.name}>
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="codebase-new-name"
          />
        </Field>
        <Field label={cs.fields.repoUrl}>
          <input
            className={inputClass}
            value={repoUrl}
            onChange={(e) => setRepoUrl(e.target.value)}
            placeholder="github.com/org/repo"
          />
        </Field>
        <Field label={cs.fields.purpose}>
          <input
            className={inputClass}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <Field label={cs.fields.stack}>
          <input
            className={inputClass}
            value={stack}
            onChange={(e) => setStack(e.target.value)}
            placeholder="React, Vite"
          />
        </Field>
        <Field label={cs.fields.defaultBranch}>
          <input
            className={inputClass}
            value={defaultBranch}
            onChange={(e) => setDefaultBranch(e.target.value)}
          />
        </Field>
        <Field label={cs.fields.devBranch}>
          <input
            className={inputClass}
            value={devBranch}
            onChange={(e) => setDevBranch(e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-3">
        <Field label={cs.codebases.dod}>
          <textarea
            className={inputClass}
            rows={4}
            value={dodText}
            onChange={(e) => setDodText(e.target.value)}
          />
        </Field>
        <div
          className="mt-1 text-[11px]"
          style={{ color: "var(--text-tertiary)" }}
        >
          {cs.codebases.dodHint}
        </div>
      </div>

      <LinkPicker
        projectId={project.id}
        appIds={appIds}
        envIds={envIds}
        onAppIds={setAppIds}
        onEnvIds={setEnvIds}
      />

      {error ? (
        <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
          ⚠ {error}
        </div>
      ) : null}
      <div className="mt-3 flex justify-end gap-2">
        <Btn onClick={onClose}>{cs.common.cancel}</Btn>
        <Btn
          variant="primary"
          disabled={name.trim().length < 2 || mutation.isPending}
          onClick={() => mutation.mutate()}
          data-testid="codebase-new-save"
        >
          {cs.common.create}
        </Btn>
      </div>
    </Panel>
  );
}

/** Přepínací výběr aplikací a environmentů codebase (nová i úprava). */
function LinkPicker({
  projectId,
  appIds,
  envIds,
  onAppIds,
  onEnvIds,
}: {
  projectId: string;
  appIds: string[];
  envIds: string[];
  onAppIds: (ids: string[]) => void;
  onEnvIds: (ids: string[]) => void;
}) {
  const applications = useApplications(projectId);
  const environments = useEnvironments(projectId);
  return (
    <>
      <ToggleChips
        label="APLIKACE V TOMTO REPU"
        options={(applications.data?.applications ?? []).map((a) => ({
          id: a.id,
          label: a.name,
        }))}
        selected={appIds}
        onChange={onAppIds}
        testId="codebase-app-toggle"
      />
      <ToggleChips
        label="ENVIRONMENTS"
        options={(environments.data?.environments ?? []).map((e) => ({
          id: e.id,
          label: e.key,
        }))}
        selected={envIds}
        onChange={onEnvIds}
        testId="codebase-env-toggle"
      />
    </>
  );
}

function ToggleChips({
  label,
  options,
  selected,
  onChange,
  testId,
}: {
  label: string;
  options: { id: string; label: string }[];
  selected: string[];
  onChange: (ids: string[]) => void;
  testId: string;
}) {
  if (options.length === 0) return null;
  return (
    <div className="mt-3">
      <div className="af-label-dim mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const on = selected.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              aria-pressed={on}
              onClick={() =>
                onChange(
                  on ? selected.filter((x) => x !== o.id) : [...selected, o.id],
                )
              }
              className="cursor-pointer px-2 py-1 text-[11px]"
              style={{
                borderRadius: "var(--radius-box)",
                border: `1px solid ${on ? "color-mix(in srgb, var(--accent) 50%, transparent)" : "var(--border-default)"}`,
                color: on ? "var(--accent)" : "var(--text-secondary)",
              }}
              data-testid={testId}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
